import flatten from 'flat';
import type { ObjectSchema } from 'yup';
import type { ObjectShape } from 'yup/lib/object';
import type { SchemaDescription, SchemaObjectDescription } from 'yup/lib/schema';
import { Formik as FormikBase, FormikConfig, FormikValues, useFormikContext } from 'formik';
import { createContext, useContext, useEffect } from 'react';

function Formik<Values extends FormikValues = FormikValues>({
  children,
  ...props
}: FormikConfig<Values>) {
  return (
    <FormikBase validateOnBlur validateOnChange={false} {...props}>
      {(formikProps) => (
        <>
          <FormikTouchedPatch />
          <FormikScrollToErrors />
          <YupRequiredFieldsProvider schema={props.validationSchema}>
            {children instanceof Function ? children(formikProps) : children}
          </YupRequiredFieldsProvider>
        </>
      )}
    </FormikBase>
  );
}

const FormikTouchedPatch = () => {
  const { errors, isSubmitting, isValidating, setFieldTouched } = useFormikContext();

  useEffect(() => {
    if (isSubmitting && !isValidating) {
      for (const path of Object.keys(flatten(errors))) {
        setFieldTouched(path, true, false);
      }
    }
  }, [errors, isSubmitting, isValidating, setFieldTouched]);

  return null;
};

const FormikScrollToErrors = () => {
  const { errors, isSubmitting, isValidating } = useFormikContext();

  useEffect(() => {
    if (isSubmitting && isValidating) {
      const errorKeys = Object.keys(flatten(errors));
      for (const errorPath of errorKeys) {
        const el =
          document.getElementsByName(errorPath)[0] ||
          document.querySelector(`[data-name='${errorPath}']`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
          el.focus({ preventScroll: true });
          break;
        }
      }
    }
  }, [errors, isSubmitting, isValidating]);

  return null;
};

const YupRequiredFieldsContext = createContext<string[]>([]);
const YupRequiredFieldsProvider: React.FC<YupRequiredFieldsProviderProps> = ({
  schema,
  children,
}) => {
  const requiredFields = schema ? extractYupRequiredFields(schema.describe()) : [];

  return (
    <YupRequiredFieldsContext.Provider value={requiredFields}>
      {children}
    </YupRequiredFieldsContext.Provider>
  );
};
type YupRequiredFieldsProviderProps = {
  children: React.ReactNode;
  schema?: ObjectSchema<ObjectShape>;
};

export const useYupRequiredFields = () => {
  const yupRequiredFieldsContext = useContext(YupRequiredFieldsContext);
  return yupRequiredFieldsContext;
};

// Helpers
function extractYupRequiredFields(schemaDesc: SchemaObjectDescription, keyPrefix = '') {
  const requiredFields: string[] = [];
  const fields = schemaDesc.fields;

  for (const key in fields) {
    if (fields.hasOwnProperty(key)) {
      const path = [keyPrefix, key].filter(Boolean).join('.');
      let fieldValidationSchema = fields[key];

      if (!fieldValidationSchema) {
        continue;
      }

      if ('innerType' in fieldValidationSchema && fieldValidationSchema.innerType) {
        fieldValidationSchema = fieldValidationSchema.innerType;
      }

      if (
        fieldValidationSchema.type === 'object' &&
        Object.prototype.hasOwnProperty.call(fieldValidationSchema, 'fields')
      ) {
        requiredFields.push(
          ...extractYupRequiredFields(fieldValidationSchema as SchemaObjectDescription, path),
        );
      }

      const isRequired = !!(fieldValidationSchema as SchemaDescription | undefined)?.tests.find(
        (test) => test.name === 'required',
      );

      if (isRequired) {
        requiredFields.push(path);
      }
    }
  }

  return requiredFields;
}

export function isRequiredYupField(requiredFields: string[], name: string) {
  return requiredFields.includes(
    name
      .split('.')
      .filter((n) => !Number.isInteger(Number(n)))
      .join('.'),
  );
}

export function convertPathToDotNotation(path: string) {
  return path.replace(/\[/g, '.').replace(/]/g, '');
}

export default Formik;
